/** @file nudge: the arrow-key nudge of the selection (1 px or 1 degree, Shift x10), one clip.setPose each. */
import { screenDeltaToWorld } from '@zilo/pose3d-render-core/camera/screenDeltaToWorld';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { applyDragToKey } from './applyDragToKey';
import { editTarget } from './editTarget';
import { keyFrameDrag } from './keyFrameDrag';
import { parentRotation } from './parentRotation';
import { setPoseCommand } from './setPoseCommand';
import { startKeyOf } from './startKeyOf';
import type { WorldDrag } from './viewportDrag';
import type { ViewportCtx } from './viewportCtx';

/** Arrow direction as screen x / y steps (y down). */
const STEP: Record<'left' | 'right' | 'up' | 'down', [number, number]> = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };

/** The world gesture of one nudge: a view-plane move of `n` px or an `n` degree turn (yaw for left/right). */
function nudgeDrag(tool: 'move' | 'rotate', [dx, dy]: [number, number], n: number, view: View): WorldDrag {
  return tool === 'move'
    ? { kind: 'move', delta: screenDeltaToWorld(dx * n, dy * n, view) }
    : { kind: 'rotate', axis: dx !== 0 ? [0, 0, -1] : view.basis.right, deltaDeg: (dx !== 0 ? dx : dy) * n };
}

/**
 * Move tool: the selection moves `n` target pixels in the view plane; rotate tool: left/right turn it `n` degrees
 * about the up axis (yaw), up/down about the view's right axis; `n` = 1, or 10 with `big`. Other tools do nothing.
 */
export function nudge(ctx: ViewportCtx, dir: keyof typeof STEP, big: boolean): void {
  const s = ctx.store.getState();
  const p = ctx.puppet();
  if (p === null || ctx.scene === null || s.selection.nodes.length === 0 || (s.tool !== 'move' && s.tool !== 'rotate')) return;
  const target = editTarget(s);
  if (target === null) return;
  const drag = nudgeDrag(s.tool, STEP[dir], big ? 10 : 1, ctx.scene.camera.view());
  const state = p.instance.state();
  const keys = Object.fromEntries(s.selection.nodes.flatMap((name) => {
    const i = p.names.indexOf(name);
    if (i < 0) return [];
    const parentQ = parentRotation(state.pose, i, state.facingDeg);
    return [[name, applyDragToKey(startKeyOf(target.clip, name, target.frame), keyFrameDrag(drag, 'world', null, parentQ), 'world', 0)]];
  }));
  void s.dispatch([setPoseCommand(target.clip, target.frame, keys)], 'Nudge');
}
